import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

// Light / dark theming. The palette lives in tokens.css: the base :root is the
// warm-light theme, and :root[data-theme="dark"] overrides the surface/ink/accent
// tokens. We persist an explicit choice in localStorage; until the user picks,
// we follow the OS (prefers-color-scheme). The matching no-FOUC bootstrap that
// sets data-theme before first paint lives inline in index.html — keep the
// storage key and theme-color values here in sync with it.

const THEME_KEY = "affinage.theme";
const META_COLOR = { light: "#f0ebe1", dark: "#0d0c0a" } as const;

export type Theme = "light" | "dark";

type ThemeState = {
  theme: Theme;
  toggle: () => void;
  setTheme: (t: Theme) => void;
};

const ThemeCtx = createContext<ThemeState | null>(null);

function systemPrefersDark(): boolean {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
}

function readStored(): Theme | null {
  try {
    const v = window.localStorage.getItem(THEME_KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch {
    return null;
  }
}

function applyTheme(theme: Theme) {
  document.documentElement.setAttribute("data-theme", theme);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", META_COLOR[theme]);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(
    () => readStored() ?? (systemPrefersDark() ? "dark" : "light")
  );

  // Reflect the current theme onto the document (and browser/iOS chrome colour).
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Track the OS setting until the user makes an explicit choice.
  useEffect(() => {
    if (readStored()) return;
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia("(prefers-color-scheme: dark)");
    } catch {
      return;
    }
    const onChange = (e: MediaQueryListEvent) => {
      if (!readStored()) setThemeState(e.matches ? "dark" : "light");
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const persist = useCallback((t: Theme) => {
    try {
      window.localStorage.setItem(THEME_KEY, t);
    } catch {
      // localStorage unavailable; ignore
    }
  }, []);

  const setTheme = useCallback(
    (t: Theme) => {
      persist(t);
      setThemeState(t);
    },
    [persist]
  );

  const toggle = useCallback(() => {
    setThemeState((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      persist(next);
      return next;
    });
  }, [persist]);

  const value = useMemo<ThemeState>(
    () => ({ theme, toggle, setTheme }),
    [theme, toggle, setTheme]
  );

  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeCtx);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
